import { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Empty, Pagination, Space, Table, Tooltip } from 'antd';
import { OverridesManager } from '../overrides/OverridesManager.js';
import type { ColumnsType } from 'antd/es/table';
import { CopyOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { apiGet, apiPost } from '../api.js';
import { TableToolbar } from '../ui/TableToolbar.js';
import CreateSiteDrawer from './CreateSiteDrawer.js';
import { SkeletonPage } from '../ui/SkeletonPage.js';
import { useUrlPagination } from '../hooks/useUrlPagination.js';

type SiteRow = { siteKey: string; domain: string | null; liveVersion: number };
type SiteListResponse = SiteRow[] | { items: SiteRow[]; total: number };

function isPaged(res: SiteListResponse): res is { items: SiteRow[]; total: number } {
  return !Array.isArray(res);
}

export function SitesPage({ registryUrl, cdnUrl }: { registryUrl: string; cdnUrl: string }) {
  const { message, modal } = App.useApp();
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const { page, pageSize, q, setPage, setPageSize } = useUrlPagination(50);
  const [loading, setLoading] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [showOverrides, setShowOverrides] = useState(false);
  const [overrideQuery, setOverrideQuery] = useState('');

  const sriUrl = cdnUrl ? `${cdnUrl}.sri` : '';

  const load = useCallback(
    async (ofs: number, lim: number) => {
      try {
        setLoading(true);
        const url = new URL(`${registryUrl}/admin/sites`);
        url.searchParams.set('limit', String(lim));
        url.searchParams.set('offset', String(ofs));
        const res = await apiGet<SiteListResponse>(url.toString());
        if (isPaged(res)) {
          setSites(res.items);
          setTotal(res.total);
        } else {
          setSites(res);
          setTotal(res.length);
        }
      } catch {
        setSites([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [registryUrl],
  );

  useEffect(() => {
    const ofs = (page - 1) * pageSize;
    load(ofs, pageSize).catch(() => undefined);
  }, [load, page, pageSize]);

  // Deep link: ?overrideHost=example.com to open Overrides drawer prefilled
  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      const host = u.searchParams.get('overrideHost');
      if (host) {
        setOverrideQuery(host);
        setShowOverrides(true);
        u.searchParams.delete('overrideHost');
        window.history.replaceState({}, '', u.toString());
      }
    } catch {
      /* ignore */
    }
    // do once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return sites;
    return sites.filter(
      (s) =>
        s.siteKey.toLowerCase().includes(query) || (s.domain ?? '').toLowerCase().includes(query),
    );
  }, [sites, q]);

  const copySnippet = async (siteKey: string) => {
    let sri = '';
    if (sriUrl) {
      try {
        const r = await fetch(sriUrl, { cache: 'no-store' });
        if (r.ok) sri = (await r.text()).trim();
      } catch {
        /* ignore */
      }
    }
    const lines = [
      '<script',
      `  src="${cdnUrl}"`,
      `  data-site-key="${siteKey}"`,
      `  data-config-url="${registryUrl}"`,
      sri ? `  integrity="${sri}"` : '  /* integrity="sha256-..." */',
      '  crossorigin="anonymous"',
      '  defer>',
      '</script>',
    ];
    await navigator.clipboard.writeText(lines.join('\n'));
    message.success('Snippet copied');
  };

  const copyVerify = async (site: SiteRow) => {
    const url = site.domain ? `https://${site.domain}` : 'https://example.com';
    const cmds = [
      '# HEADERS (HSTS/CSP/Referrer-Policy/Permissions-Policy/NoSniff)',
      `curl -sSI ${url} | egrep -i "strict-transport|content-security|referrer-policy|permissions-policy|x-content-type|x-frame"`,
      '',
      '# SERVER-SET COOKIES (flags)',
      `curl -sS -I -D - ${url} | grep -i '^Set-Cookie'`,
      '',
      '# SCANNER (pre/post, with your Registry URL)',
      `CMP_REGISTRY_URL=${registryUrl} pnpm --filter cmp-scanner scan ${url} tmp/report.json`,
    ].join('\n');
    await navigator.clipboard.writeText(cmds);
    message.success('Verification commands copied');
  };

  const rotateKey = (siteKey: string) => {
    modal.confirm({
      title: 'Rotate site key?',
      content: 'You will need to update installed snippets after rotation.',
      okText: 'Rotate',
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusyKey(siteKey);
        try {
          await apiPost(
            `${registryUrl}/admin/sites/by-key/${encodeURIComponent(siteKey)}/rotate-key`,
          );
          await load(offset, limit);
          message.success('Site key rotated');
        } catch {
          message.error('Failed to rotate key');
        } finally {
          setBusyKey(null);
        }
      },
    });
  };

  const onPage = async (p: number, s: number) => {
    setPage(p);
    if (s !== pageSize) setPageSize(s);
  };

  const columns: ColumnsType<SiteRow> = [
    {
      title: 'Site Key',
      dataIndex: 'siteKey',
      key: 'siteKey',
      render: (v: string) => <code>{v}</code>,
    },
    { title: 'Domain', dataIndex: 'domain', key: 'domain', render: (d: string | null) => d ?? '—' },
    {
      title: 'Live Version',
      dataIndex: 'liveVersion',
      key: 'liveVersion',
      width: 140,
      align: 'center',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 360,
      render: (_: unknown, s: SiteRow) => (
        <Space.Compact>
          <Tooltip title="Copy drop-in snippet">
            <Button onClick={() => copySnippet(s.siteKey)} icon={<CopyOutlined />} />
          </Tooltip>
          <Tooltip title="Copy verification commands">
            <Button onClick={() => copyVerify(s)} icon={<SafetyCertificateOutlined />} />
          </Tooltip>
          <Tooltip title="Rotate site key">
            <Button
              danger
              loading={busyKey === s.siteKey}
              onClick={() => rotateKey(s.siteKey)}
              icon={<ReloadOutlined />}
            />
          </Tooltip>
        </Space.Compact>
      ),
    },
  ];

  const current = page;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <TableToolbar
        placeholder="Search by key or domain"
        extra={
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            Create Site
          </Button>
        }
      />

      {loading ? (
        <SkeletonPage />
      ) : filtered.length === 0 && !q ? (
        <Empty description="No sites yet" />
      ) : (
        <>
          <Table<SiteRow>
            rowKey="siteKey"
            dataSource={filtered}
            columns={columns}
            pagination={false}
            size="middle"
          />

          <Pagination
            current={current}
            total={total}
            pageSize={pageSize}
            showSizeChanger
            pageSizeOptions={[25, 50, 100]}
            onChange={onPage}
            style={{ marginLeft: 'auto' }}
          />
        </>
      )}
      {showOverrides && (
        <OverridesManager
          registryUrl={registryUrl}
          onClose={() => setShowOverrides(false)}
          initialQuery={overrideQuery}
        />
      )}
      <CreateSiteDrawer open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
